import {useState, useRef} from "react";
import {connect} from "react-redux";
import {Button, styled} from "@reside/ui";

import {SortDirection} from "../../store/reporting-reports/reporting-reports.common";
import {AdminAdmissionsTable} from "../../atoms/admin-admissions-table";
import {
  DashboardBoardScrollArea,
  DashboardBoardScrollShell,
} from "../../atoms/dashboard-table/DashboardBoardScroll";
import {DashboardBoardScrollMinimap} from "../../atoms/dashboard-table/DashboardBoardScrollMinimap";
import {useWorkflowsQuery} from "../../atoms/dashboard-table/hooks/useWorkflowsQuery";
import {useAdmissionsByStatusQueryUtils} from "../../atoms/dashboard-table/hooks";
import type {
  SearchRequest,
  WorkflowStatus,
} from "../../services/WorkflowsService";
import type {AdmissionFromSearch} from "../../services/AdmissionsService";
import {updateTableKey} from "../../atoms/list-table/helpers";
import {PageAdminAdmissionsProps} from "./utils/types";
import {Status} from "./filters/utils/types";
import {isResideAdmin} from "../../utils/helpers/users";
import {Spinner} from "../../atoms/spinner";
import {select, AppState} from "../../store";
import {LoadMoreWrapper} from "./styles";

/** Horizontal minimap segments (visual only; not tied to table column count). */
const ADMISSIONS_LIST_MINIMAP_TICKS = 6;

/** One Load More. Larger than a board column's page: a table row is a line, not a card. */
const LIST_PAGE_SIZE = 30;

/** What the table opens on, and one of the keys the service sorts by. */
const DEFAULT_SORT_BY = "dateOfAdmission";

const ListError = styled.div`
  padding: ${({theme}) => theme.space[5]}px ${({theme}) => theme.space[4]}px;
  color: ${({theme}) => theme.color.gray100};
  font-size: ${({theme}) => theme.fontSize[2]}px;
  /* Wraps rather than widening the board when the term is long. */
  overflow-wrap: anywhere;
`;

const ListBoardInner = styled.div`
  box-sizing: border-box;
  padding-left: var(--dashboard-h-pad);
  padding-right: var(--dashboard-h-pad);
  /* Wider than the viewport when columns don't fit — pairs with DashboardBoardScrollMinimap. */
  width: max-content;
  min-width: 100%;

  .lt-clip,
  .list-table {
    width: max-content;
    min-width: 100%;
  }
`;

/**
 * Page that will display all admissions and enable filtering by facilities and status.
 * If user wants to filter by facilites using dropdown @activeFacilitiesTreeKeys array is filled and used for filtering.
 * If logged in user is not allowed to see the dropdown by being allowed to see admissions belonging only to his facility,@hasSelectedFacility will check if that facility exists on array of facilities he is part of.
 * @isAllowedToSeeAdmissions prop will prevent unwanted users from seeing all admissions.
 *
 * */

type RendererProps = PageAdminAdmissionsProps & {
  admissionStatus: Status;
  selectItem: (item: Status) => void;
  starFilterActive?: boolean;
  workflowType?: string | null;
  /** Dashboard search term. Filters the table, as it does the board. */
  query?: string | null;
};

/**
 * Exported unconnected for tests: the connected component needs a redux store for nothing more
 * than the facility keys, which the tests pass directly.
 */
export const DashboardAdmissionsListRenderer = ({
  activeFacilitiesTreeKeys,
  hasSelectedFacilities,
  isAllowedToSeeAdmissions,
  admissionStatus,
  selectItem,
  starFilterActive,
  workflowType,
  query,
}: RendererProps) => {
  const listScrollRef = useRef<HTMLDivElement | null>(null);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT_BY);
  const [sortDirection, setSortDirection] = useState(SortDirection.ASC);

  const {refetchAllAdmissionsByStatusQueries, refetchAfterReplication} =
    useAdmissionsByStatusQueryUtils();
  const {
    data,
    hasNextPage,
    fetchNextPage,
    refetch,
    isLoading,
    isFetchingNextPage,
    status: queryStatus,
    appliedQuery,
  } = useWorkflowsQuery({
    sortBy: sortBy as SearchRequest["sortBy"],
    // NONE is unreachable from the table -- it only ever emits ASC or DESC -- but it is in the
    // enum, and the service rejects anything outside its own two with a 422.
    sortDirection:
      sortDirection === SortDirection.NONE
        ? undefined
        : (sortDirection as SearchRequest["sortDirection"]),
    status: admissionStatus.id as WorkflowStatus | null,
    facilityIds: activeFacilitiesTreeKeys,
    pageSize: LIST_PAGE_SIZE,
    isAllowedToSeeAdmissions,
    starredOnly: starFilterActive,
    query,
    workflowType,
  });

  /*
   * Cast rather than mapped: the card's field names are already the ones the table's cells read
   * -- code, status, completionPercentage, flags, resources, answers, created, updated. The one
   * it does not carry is `starred`; see the star column in AdminAdmissionsTable.
   */
  const admissions = data?.pages.flatMap(page => page.cards) as unknown as
    | ReadonlyArray<AdmissionFromSearch>
    | undefined;
  // Every page carries the same total for these filters; the first is as good as the last.
  const [{total: totalElements = 0, totalIsCapped = false} = {}] =
    data?.pages ?? [];
  const displayedCount =
    hasSelectedFacilities && admissions ? admissions.length : 0;
  /*
   * From react-query's status, not from "no rows and not fetching": a 500 and a 422 both leave
   * the list empty, and the table's own "No Workflows found with these parameters..." would
   * present a failure as an empty result.
   */
  const showError = hasSelectedFacilities && queryStatus === "error";

  const onSort = (sortBy: string, sortDirection: SortDirection) => {
    setSortBy(sortBy);
    setSortDirection(sortDirection || SortDirection.NONE);
  };

  return (
    <DashboardBoardScrollShell>
      <DashboardBoardScrollArea ref={listScrollRef}>
        {isLoading ? (
          <Spinner />
        ) : showError ? (
          <ListBoardInner>
            <ListError data-test-id="admissions-list-error">
              {/* appliedQuery, not the raw prop: below the minimum length no term is sent, so
                  naming it here would blame a search that never ran. */}
              {appliedQuery
                ? `Workflows matching "${appliedQuery}" could not be loaded. Try again.`
                : "Workflows could not be loaded. Try again."}
            </ListError>
          </ListBoardInner>
        ) : (
          <ListBoardInner>
            <AdminAdmissionsTable
              admissions={
                hasSelectedFacilities && admissions ? (admissions as any) : []
              }
              onSort={onSort}
              sortBy={sortBy}
              sortDirection={sortDirection}
              tableName={updateTableKey(admissionStatus, "")}
              // Delete and restore go through Java while this list reads the replica, so a plain
              // refetch lands inside the replication window and shows the row unchanged. Both
              // only change the status: a list filtered by status loses the row, so it is held
              // back; "All" keeps it, so the refetch just waits out replication.
              onAdmissionUpdated={admissionId =>
                admissionStatus.id
                  ? refetchAllAdmissionsByStatusQueries(admissionId)
                  : refetchAfterReplication()
              }
              // Set Workflow keeps the row and only changes its badge, so the row is not held
              // back; the refetch just waits out replication.
              onWorkflowSet={refetchAfterReplication}
              isAllowedToSeeAdmissions={isAllowedToSeeAdmissions}
            />
            {hasNextPage &&
              hasSelectedFacilities &&
              isAllowedToSeeAdmissions && (
                <LoadMoreWrapper>
                  <Button
                    size="medium"
                    color="primary"
                    disabled={
                      isFetchingNextPage ||
                      (activeFacilitiesTreeKeys &&
                        activeFacilitiesTreeKeys.length === 0)
                    }
                    onClick={() => fetchNextPage()}
                  >
                    {isFetchingNextPage ? "Loading..." : "Load More"}
                  </Button>
                  {/* The service stops counting at its cap, so a large result shows "10,000+". */}
                  <span>{`${displayedCount} of ${
                    hasSelectedFacilities ? totalElements.toLocaleString() : 0
                  }${hasSelectedFacilities && totalIsCapped ? "+" : ""}`}</span>
                </LoadMoreWrapper>
              )}
          </ListBoardInner>
        )}
      </DashboardBoardScrollArea>
      {!isLoading && (
        <DashboardBoardScrollMinimap
          scrollRef={listScrollRef}
          columnCount={ADMISSIONS_LIST_MINIMAP_TICKS}
          tickLayout="horizontal"
        />
      )}
    </DashboardBoardScrollShell>
  );
};

const mapState = (state: AppState) => {
  const {facilities} = select.adminSession.user(state);

  return {
    activeFacilitiesTreeKeys:
      select.adminSession.activeFacilitiesTreeKeys(state),
    hasSelectedFacilities: select.adminSession.hasSelectedFacilities(state),
    isAllowedToSeeAdmissions:
      Boolean(facilities.length) ||
      isResideAdmin(select.adminSession.user(state)),
  };
};

export const DashboardAdmissionsListView = connect(mapState)(
  DashboardAdmissionsListRenderer,
);
